"use client"

import * as React from "react"
import Image from "next/image"

import type { PrizeKind, WheelSegment } from "@/features/seasonal/lucky-draw"
import type { ThemeId } from "@/features/seasonal/themes"
import { WORD_C, layoutSlice } from "@/features/seasonal/wheel-layout"
import { cn } from "@/lib/utils"

/** Treats in the shop's orange and acid, tricks in Halloween purple. */
export const SEGMENT_COLOURS: Record<PrizeKind, { fill: string; alt: string; text: string }> = {
  coupon: { fill: "#ff6a1f", alt: "#ff8a3d", text: "#170a04" },
  gift: { fill: "#c8f23a", alt: "#d9ff5c", text: "#11160a" },
  nothing: { fill: "#4b1f7d", alt: "#5d2a96", text: "#f7f4ed" },
}

/** Treat or Trick in a colour of its own, so it never runs into the prize below it. */
const WORD_INK: Record<PrizeKind, string> = {
  coupon: "#3a1366",
  gift: "#5b21b6",
  nothing: "#ffb347",
}

type Palette = {
  segments: typeof SEGMENT_COLOURS
  ink: typeof WORD_INK
  /** The rim, the hub's ring and the pointer. */
  trim: string
  /** The light behind the rim, as "r g b". */
  glow: string
}

const PALETTES: Record<"usual" | ThemeId, Palette> = {
  usual: { segments: SEGMENT_COLOURS, ink: WORD_INK, trim: "#ff6a1f", glow: "255 106 31" },
  halloween: { segments: SEGMENT_COLOURS, ink: WORD_INK, trim: "#ff6a1f", glow: "255 106 31" },
  // Diwali: treats in gold and rose, tricks in deep plum, trimmed in gold.
  diwali: {
    segments: {
      coupon: { fill: "#ffb52e", alt: "#ffc95c", text: "#2a0a16" },
      gift: { fill: "#e0337a", alt: "#ec5a95", text: "#fff6e0" },
      nothing: { fill: "#4a0f2e", alt: "#5e1640", text: "#ffe7a8" },
    },
    ink: { coupon: "#7d1745", gift: "#ffe08a", nothing: "#ffb52e" },
    trim: "#ffc94a",
    glow: "255 196 70",
  },
}

const R = 90
const BULBS = 24
/** Seconds for the light to run once round the rim. */
const BULB_LAP = 2.4

// Clockwise from the top, in degrees, as landingAngle counts.
const point = (deg: number, r: number) => {
  const rad = ((deg - 90) * Math.PI) / 180
  return { x: r * Math.cos(rad), y: r * Math.sin(rad) }
}

function slice(from: number, to: number): string {
  const a = point(from, R)
  const b = point(to, R)
  const large = to - from > 180 ? 1 : 0
  return `M0 0 L${a.x.toFixed(3)} ${a.y.toFixed(3)} A${R} ${R} 0 ${large} 1 ${b.x.toFixed(3)} ${b.y.toFixed(3)}Z`
}

/**
 * The wheel. `rotation` turns it (degrees, clockwise); with `spinning` the turn is animated on
 * the compositor and `onSettled` fires when it stops. The pointer stays at the top.
 */
export function LuckyWheel({
  segments,
  rotation = 0,
  spinning = false,
  onSettled,
  className,
  season = null,
}: {
  /** Whose colours: a seasonal look's, or the usual. */
  season?: ThemeId | null
  segments: WheelSegment[]
  rotation?: number
  spinning?: boolean
  onSettled?: () => void
  className?: string
}) {
  const palette = PALETTES[season ?? "usual"]
  const count = Math.max(1, segments.length)
  const seg = 360 / count

  return (
    <div className={cn("relative aspect-square w-full", className)}>
      {/* Glow behind the rim. */}
      <div
        aria-hidden
        className="absolute inset-[4%] rounded-full blur-2xl"
        style={{
          background: `radial-gradient(circle, rgb(${palette.glow} / 0.45), transparent 68%)`,
        }}
      />
      {/* Clipped to the circle: a turned square's corners would otherwise poke out of the box. */}
      <div className="absolute inset-0 overflow-hidden rounded-full">
        <div
          className="lucky-wheel absolute inset-0"
          style={{
            transform: `rotate(${rotation}deg)`,
            transition: spinning ? "transform 5.2s cubic-bezier(0.12, 0.72, 0.1, 1)" : "none",
          }}
          onTransitionEnd={(e) => {
            if (e.propertyName === "transform") onSettled?.()
          }}
        >
          <svg viewBox="-100 -100 200 200" className="size-full" role="img" aria-hidden>
            <circle r="99" fill="#1a1219" />
            <circle r="97" fill="none" stroke={palette.trim} strokeWidth="2" opacity="0.9" />
            {segments.map((s, i) => {
              const colours = palette.segments[s.kind]
              const mid = (i + 0.5) * seg
              return (
                <g key={s.id}>
                  <path
                    d={slice(i * seg, (i + 1) * seg)}
                    fill={i % 2 ? colours.alt : colours.fill}
                    stroke="#140d14"
                    strokeWidth="1.2"
                  />
                  {(() => {
                    const prize = (s.detail || s.label).toUpperCase()
                    const fit = layoutSlice(s.label.toUpperCase(), prize, count)
                    // The prize along the slice's centre line, turned to read upright on the
                    // wheel as it first shows: on the right half it reads out towards Treat or Trick,
                    // on the left in from it. That word, across the slice, reads upright too.
                    const right = mid <= 180
                    const x = right ? fit.out : -fit.out
                    const bottom = mid > 90 && mid < 270
                    return (
                      <g transform={`rotate(${mid})`}>
                        <text
                          transform={`translate(0 ${-WORD_C}) rotate(${bottom ? 180 : 0})`}
                          textAnchor="middle"
                          dominantBaseline="central"
                          fill={palette.ink[s.kind]}
                          className="font-display"
                          style={{ fontSize: fit.word.size, letterSpacing: "0.1em" }}
                        >
                          {s.label.toUpperCase()}
                        </text>
                        <g transform={`rotate(${right ? -90 : 90})`}>
                          {fit.lines.map((line, j) => (
                            <text
                              key={j}
                              x={x}
                              y={fit.lineY[j]}
                              textAnchor={right ? "end" : "start"}
                              dominantBaseline="central"
                              fill={colours.text}
                              className="font-display"
                              style={{ fontSize: fit.size, letterSpacing: "0.03em" }}
                            >
                              {line}
                            </text>
                          ))}
                        </g>
                      </g>
                    )
                  })()}
                </g>
              )
            })}
          </svg>
        </div>
      </div>
      {/* The bulbs are the wheel's frame, so they stay still while it turns; a soft light runs
          round them (globals.css). */}
      <svg
        viewBox="-100 -100 200 200"
        aria-hidden
        className="pointer-events-none absolute inset-0 size-full"
      >
        {Array.from({ length: BULBS }, (_, i) => {
          const p = point((i * 360) / BULBS, 94.5)
          return (
            <circle
              key={i}
              cx={p.x}
              cy={p.y}
              r="2.4"
              className="wheel-bulb"
              // Later delays for bulbs further round: the light runs clockwise, with the spin.
              style={{ animationDelay: `${(-BULB_LAP * (BULBS - i)) / BULBS}s` }}
            />
          )
        })}
      </svg>
      {/* The hub stays still while the wheel turns under it. */}
      <div
        className="absolute top-1/2 left-1/2 flex size-[21%] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-[3px] bg-[#120c12]"
        style={{
          borderColor: palette.trim,
          boxShadow: `0 0 18px rgb(${palette.glow} / 0.45)`,
        }}
      >
        <Image
          src="/icon.png"
          alt=""
          width={64}
          height={64}
          className="size-[66%] object-contain"
        />
      </div>
      {/* The pointer, fixed at the top. */}
      <svg
        viewBox="0 0 40 44"
        aria-hidden
        className="absolute top-[-3%] left-1/2 w-[11%] -translate-x-1/2 drop-shadow-[0_4px_10px_rgb(0_0_0_/_0.6)]"
      >
        <path
          d="M4 3 H36 L20 41 Z"
          fill={palette.trim}
          stroke="#170a04"
          strokeWidth="3"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  )
}
